// Voice Mode 2.0 — P1: energy-based voice activity detection.
//
// Two jobs, both clock-injected (so this is trivially testable and never
// depends on wall-clock flakiness):
//   1. **endpointing** — did the user start/stop speaking? (drives the ring)
//   2. **barge-in** — did the user speak *while the assistant was talking*?
//
// It is deliberately simple: RMS threshold above an adaptive-ish floor with a
// minimum speech run and a silence hangover. The browser's own STT still owns
// transcription; this only decides "speech is happening now".

import { rms } from './pcm';

export type VadSignal = 'speech_start' | 'speech_end' | 'barge_in';

export interface VadOptions {
  /** RMS above this counts as speech (default 0.02 ≈ quiet room + close mic). */
  threshold?: number;
  /** Silence needed to close an utterance (ms) — server endpointing default 500. */
  endpointingMs?: number;
  /** Continuous speech needed to open an utterance (ms). */
  minSpeechMs?: number;
  /** Continuous speech needed to count as barge-in over playback (ms). */
  bargeInMs?: number;
  /** Mic sample rate, used to turn frame sizes into milliseconds. */
  sampleRate?: number;
}

export class VoiceActivityDetector {
  readonly threshold: number;
  readonly endpointingMs: number;
  readonly minSpeechMs: number;
  readonly bargeInMs: number;
  readonly sampleRate: number;

  private speechMs = 0;
  private silenceMs = 0;
  private bargedIn = false;
  private lastLevel = 0;
  private speakingNow = false;
  private playbackActive = false;

  constructor(options: VadOptions = {}) {
    this.threshold = options.threshold ?? 0.02;
    this.endpointingMs = options.endpointingMs ?? 500;
    this.minSpeechMs = options.minSpeechMs ?? 120;
    this.bargeInMs = options.bargeInMs ?? 200;
    this.sampleRate = options.sampleRate ?? 48000;
  }

  get speaking(): boolean {
    return this.speakingNow;
  }

  get level(): number {
    return this.lastLevel;
  }

  /** Tell the detector whether agent audio is currently playing. */
  setPlaybackActive(active: boolean): void {
    if (!active && this.playbackActive) this.bargedIn = false;
    this.playbackActive = active;
  }

  reset(): void {
    this.speechMs = 0;
    this.silenceMs = 0;
    this.bargedIn = false;
    this.speakingNow = false;
  }

  /**
   * Feed one frame of mic audio. Frame duration is derived from the sample
   * count, so the detector needs no external clock.
   */
  feed(samples: Float32Array): VadSignal | null {
    const level = rms(samples);
    this.lastLevel = level;
    const voiced = level >= this.threshold;

    if (voiced) {
      this.speechMs += this.durationOf(samples);
      this.silenceMs = 0;
    } else {
      this.silenceMs += this.durationOf(samples);
      this.speechMs = 0;
    }

    // Barge-in: sustained speech while we are the ones talking.
    if (this.playbackActive && voiced && !this.bargedIn && this.speechMs >= this.bargeInMs) {
      this.bargedIn = true;
      return 'barge_in';
    }

    if (!this.speakingNow) {
      if (voiced && this.speechMs >= this.minSpeechMs) {
        this.speakingNow = true;
        this.silenceMs = 0;
        return 'speech_start';
      }
      return null;
    }

    if (!voiced && this.silenceMs >= this.endpointingMs) {
      this.speakingNow = false;
      this.speechMs = 0;
      return 'speech_end';
    }
    return null;
  }

  /** Frame duration in ms from the sample count (no external clock needed). */
  private durationOf(samples: Float32Array): number {
    return (samples.length / this.sampleRate) * 1000;
  }
}

/** Frames of this many samples ≈ 46 ms at 44.1 kHz (AnalyserNode fftSize 2048). */
export const FRAME_SAMPLES = 2048;
